/**
 * Executive AI year window (C1) — FE-only clipping of time-series year axes.
 *
 * A model instance may define ExecAIYearsBefore / ExecAIYearsAfter (nullable).
 * When set, the Executive time-series surfaces (CV, ES proposed changes, ES
 * review results) and their Excel downloads show only the years within
 * [currentCalendarYear - before, currentCalendarYear + after], intersected
 * with whatever years the payload actually carries. NULL on either bound means
 * that side is unbounded (the global default — show everything).
 *
 * This is deliberately FE-only: the backend ships the full multi-year payload
 * and the Excel is built client-side (ag-Grid exportDataAsExcel), so the window
 * is a presentation filter, never a backend clip. It applies ONLY on the
 * Executive surfaces — the analyst charts share no code path with this helper.
 *
 * Labels are matched leniently: a 4-digit year is parsed out of each label
 * (e.g. "2028", "FY2028", "2028 Total"). A label with no parseable year is
 * ALWAYS kept — the window never hides a column it can't place on the axis, so
 * a non-year period label (or a malformed header) degrades to "shown".
 */

export interface YearWindow {
  before?: number | null;
  after?: number | null;
}

const YEAR_RE = /(\d{4})/;

const parseYear = (label: unknown): number | null => {
  if (typeof label === 'number' && Number.isInteger(label)) return label;
  const m = String(label ?? '').match(YEAR_RE);
  return m ? Number(m[1]) : null;
};

/**
 * Returns a boolean keep-mask parallel to `labels`: true = show this index.
 * `true` for every index when the window is inactive (both bounds null/absent)
 * or when a label carries no parseable year.
 */
export const yearKeepMask = (
  labels: readonly unknown[],
  window: YearWindow | null | undefined,
  now: number = new Date().getFullYear()
): boolean[] => {
  const before = window?.before;
  const after = window?.after;
  // Inactive window (both bounds unset) — keep everything.
  if (before == null && after == null) {
    return labels.map(() => true);
  }
  const lo = before == null ? -Infinity : now - before;
  const hi = after == null ? Infinity : now + after;
  return labels.map((label) => {
    const year = parseYear(label);
    if (year == null) return true; // unplaceable label -> always shown
    return year >= lo && year <= hi;
  });
};

/** True when the window actually constrains anything (at least one bound set). */
export const isYearWindowActive = (
  window: YearWindow | null | undefined
): boolean => window != null && (window.before != null || window.after != null);

/** Filter any array to the kept indices of a mask (mask must be same length). */
export const applyKeepMask = <T>(arr: readonly T[], mask: boolean[]): T[] =>
  arr.filter((_, i) => mask[i]);

/**
 * Clips a forecast-API chart payload ({ data: { Dates, PublishedData, PreviewData, ... }, request_context, ... })
 * to the year window. `Dates` and every series' `values` are positional siblings (index i of each series is the
 * value for Dates[i] — the contract the range slider, the Original/Recalculated table and the Excel export all
 * rely on), so one keep-mask built from the date labels is applied to all of them. Every other key on the payload
 * and on `data` is carried through untouched.
 *
 * Returns the SAME object (referential identity preserved, so memoized consumers don't re-init) when there is
 * nothing to do: inactive window, no dates, every date inside the window — or, deliberately, when the window would
 * remove EVERY date. That last case falls back to the full payload rather than a blank chart (ruled 2026-09-07):
 * a window misconfigured against a model whose years sit entirely outside it must not hide the executive's data.
 */
interface ClippableSeries {
  values?: unknown;
}

interface ClippableDataBlock {
  Dates?: unknown;
  PublishedData?: ClippableSeries[];
  PreviewData?: ClippableSeries[];
}

const dateLabel = (date: unknown): unknown =>
  date != null && typeof date === 'object' && 'Date' in date
    ? (date as { Date: unknown }).Date
    : date;

export const clipChartPayloadToYearWindow = <T extends Record<string, unknown>>(
  payload: T,
  window: YearWindow | null | undefined
): T => {
  if (!isYearWindowActive(window)) return payload;
  const data = payload.data as ClippableDataBlock | undefined;
  const dates = data?.Dates;
  if (!data || !Array.isArray(dates) || dates.length === 0) return payload;

  const mask = yearKeepMask(dates.map(dateLabel), window);
  if (mask.every(Boolean)) return payload; // nothing outside the window
  if (!mask.some(Boolean)) return payload; // window would empty the axis -> show everything

  const clipSeries = (series: ClippableSeries[]): ClippableSeries[] =>
    series.map((s) =>
      Array.isArray(s.values)
        ? { ...s, values: applyKeepMask(s.values, mask) }
        : s
    );

  const clipped: ClippableDataBlock = {
    ...data,
    Dates: applyKeepMask(dates, mask),
  };
  if (Array.isArray(data.PublishedData)) {
    clipped.PublishedData = clipSeries(data.PublishedData);
  }
  if (Array.isArray(data.PreviewData)) {
    clipped.PreviewData = clipSeries(data.PreviewData);
  }
  return { ...payload, data: clipped };
};
